import InfiniteScroll from 'react-infinite-scroll-component';
import { useSocket } from '../../global/socket';
import { useCallback, useMemo, useRef, useState } from 'react';
import { ApiGame } from '../../../src/types/apigame';
import { ClientRequests } from '../../../src/enum/socket';
import { GetGamesForUserParams, GetGamesForUserType } from '../../../src/models/socket/game';
import { Spinner } from '@blueprintjs/core';
import { useSession } from 'next-auth/react';
import GamesListGame from './gameslistgame';
import styles from '../gameslist.module.css';

export const COMPLETED_GAMES_LIST_DEFAULT_PAGE_SIZE = 20;

function compareCompletedGames(a: ApiGame, b: ApiGame): number {
    return b.lastUpdated - a.lastUpdated
        || b.id - a.id;
}

function mergeCompletedGames(
    existingGames: ApiGame[],
    newGames: ApiGame[],
): ApiGame[] {
    const gameMap = new Map<number, ApiGame>();

    for (const game of existingGames) {
        gameMap.set(game.id, game);
    }

    for (const game of newGames) {
        gameMap.set(game.id, game);
    }

    return Array.from(gameMap.values())
        .sort(compareCompletedGames);
}

type Props = {
    pageSize?: number;
    initialData: ApiGame[];
};

export default function CompletedGamesList(
    {
        initialData,
        pageSize = COMPLETED_GAMES_LIST_DEFAULT_PAGE_SIZE,
    }: Props,
): JSX.Element {
    const { socket } = useSocket();
    const { data: session } = useSession();
    const isLoadingRef = useRef(false);
    const [games, setGames] = useState<ApiGame[]>(
        () => [...initialData].sort(compareCompletedGames),
    );
    const [hasMore, setHasMore] = useState(
        () => initialData.length > 0 && (initialData.length % pageSize) === 0,
    );

    const userId = Number(session.user['id']);

    const getGames = useCallback(
        async (
            cursor?: number,
        ): Promise<ApiGame[] | null> => {
            if (!socket) {
                return;
            }

            const params: GetGamesForUserParams = {
                searchType: GetGamesForUserType.Completed,
                take: pageSize,
                cursor,
            };

            const newGames = await socket.emitWithAck(
                ClientRequests.GetGames,
                params,
            );

            return newGames;
        },
        [socket, pageSize],
    );

    const nextPage = useCallback(
        async (): Promise<void> => {
            if (!socket || isLoadingRef.current || games.length === 0) {
                return;
            }

            isLoadingRef.current = true;

            try {
                const newGames = await getGames(
                    games[games.length - 1].id,
                );

                if (!newGames) {
                    setHasMore(false);
                    return;
                }

                setHasMore(newGames.length === pageSize);
                setGames((existingGames) => mergeCompletedGames(existingGames, newGames));
            } finally {
                isLoadingRef.current = false;
            }
        },
        [getGames, socket, games, pageSize],
    );

    const items = useMemo(
        () => games.map(
            (game) => <GamesListGame key={game.id} game={game} userId={userId} />
        ),
        [games, userId],
    );

    return <InfiniteScroll
        className={styles.gamesList}
        dataLength={games.length}
        next={nextPage}
        hasMore={hasMore}
        loader={<Spinner />}
        style={{ overflow: 'visible' }}
    >
        {items}
    </InfiniteScroll>;
}
